import { deepClone } from "@dashboard-v3/shared";
import { AccountManagement } from "pages/Rules/Form/Steps/CloudStorage/utils";
import initSteps from "./initSteps";
import initChecks from "./initChecks";
import changeTargetType from "./reducers/changeTargetType";
import { confirmStep } from "./reducers/confirmStep";
import { updateStep } from "./reducers/updateStep";
import { updateFilterTarget } from "./reducers/updateFilterTarget";
import { updateFilterAction } from "./reducers/updateFilterAction";
import { saveRule } from "./reducers/saveRule";
import { updateRuleLabels } from "./reducers/updateRuleLabels";
import { saveRuleFail } from "./reducers/saveRuleFail";
import { asyncValidationEnds } from "./reducers/asyncValidationEnds";
import { updateWebhookProcessing } from "./reducers/updateWebhookProcessing";
import { checkUnmanagedFlow } from "./reducers/checkUnmanagedFlow";
import { updateStorageManagement } from "./reducers/updateStorageManagement";
import { updateIncludeFolders } from "./reducers/updateIncludeFolders";
import { updateExcludeFolders } from "./reducers/updateExcludeFolders";
import { updateMoreActions } from "./reducers/updateMoreActions";
import { checkFolderCollaboration } from "./reducers/checkFolderCollaboration";
import { updateFilterPolicy } from "./reducers/updateFilterPolicy";
import { updateSendDelay } from "./reducers/updateSendDelay";
import { toggleStorageSteps } from "./reducers/toggleStorageSteps";
import { updateEditData } from "./reducers/updateEditData";
import { changeZipPasswordType } from "./reducers/changeZipPasswordType";
import { changeContentCopyType } from "./reducers/changeContentCopyType";
import { updateExecutionStatus } from "./reducers/updateExecutionStatus";

import { Rule } from "pages/Rules/common/types";
import { Action } from "./action";
import { State } from "./types";

declare global {
  interface Window {
    ruleState: State;
  }
}

export function reducer(state: State, action: Action): State {
  console.groupCollapsed(action.type);
  console.trace();
  console.log("> ", action);

  const newState = executeReducer(state, action);

  if (newState) {
    console.log("< ", newState);
    console.groupEnd();
    window.ruleState = deepClone(newState);
    return newState;
  } else {
    console.log("[WARNING] no changes or type not found");
    console.groupEnd();
    return state;
  }
}

function executeReducer(state: State, action: Action) {
  switch (action.type) {
    case "updateRuleLabels":
      return updateRuleLabels(state, action.payload);

    case "changeTargetType":
      return changeTargetType(state, action.payload);

    case "confirmStep":
      return confirmStep(state, action.payload);

    case "updateStep":
      return updateStep(state, action.payload);

    case "updateFilterTarget":
      return updateFilterTarget(state, action.payload);

    case "updateFilterAction":
      return updateFilterAction(state, action.payload);

    case "updateWebhookProcessing":
      return updateWebhookProcessing(state, action.payload);

    case "updateStorageManagement":
      return updateStorageManagement(state, action.payload);

    case "updateMoreActions":
      return updateMoreActions(state, action.payload);

    case "asyncValidationEnds":
      return asyncValidationEnds(state, action.payload);

    case "checkUnmanagedFlow":
      return checkUnmanagedFlow(state, action.payload);

    case "checkFolderCollaboration":
      return checkFolderCollaboration(state, action.payload);

    case "saveRule":
      return saveRule(state, action.payload);

    case "saveRuleFail":
      return saveRuleFail(state, action.payload);

    case "updateIncludeFolders":
      return updateIncludeFolders(state, action.payload);

    case "updateExcludeFolders":
      return updateExcludeFolders(state, action.payload);

    case "updateFilterPolicy":
      return updateFilterPolicy(state, action.payload);

    case "updateSendDelay":
      return updateSendDelay(state, action.payload);

    case "toggleStorageSteps":
      return toggleStorageSteps(state, action.payload);

    case "updateEditData":
      return updateEditData(state, action.payload);

    case "changeContentCopyType":
      return changeContentCopyType(state, action.payload);

    case "changeZipPasswordType":
      return changeZipPasswordType(state, action.payload);

    case "updateExecutionStatus":
      return updateExecutionStatus(state, action.payload);

    default:
      throw new Error("Some dispatch type was omited");
  }
}

export function initialState(rule: Rule): State {
  const isEdit = Boolean(rule.id);
  const { steps, currentStep } = initSteps(rule);

  if (isEdit) {
    const { actionType } = rule;
    const stateForEdit: State = {
      asyncStatus: "idle",
      rule,
      type: rule.actionType,
      currentStep,
      steps,
      filters: initFilterState(),
      canSave: true,
      remaining: [],
      checks: initChecks(rule),
      editData: {
        savedRule: rule,
        showUpdateConfirm: false,
        changes: [],
      },
      webhookStatus: {
        isValid: false,
        foldersInUse: [],
        missingFolder: "",
        userWithoutAccess: false,
      },
    };

    if (actionType === "ZIP_ATTACHMENTS" && "storage" in rule.zipAttachments) {
      stateForEdit.storageManagement = AccountManagement.AUTO;
    }

    return stateForEdit;
  }

  return {
    asyncStatus: "idle",
    rule,
    type: rule.actionType,
    currentStep,
    steps,
    filters: initFilterState(),
    canSave: false,
    remaining: ["name", "steps"],
    checks: initChecks(rule),
    webhookStatus: {
      isValid: false,
      foldersInUse: [],
      missingFolder: "",
      userWithoutAccess: false,
    },
  };
}

const initFilterState = () => ({
  actionFilter: { valid: true, errors: {} },
  targetFilter: { valid: true, errors: {} },
  targetFolders: { valid: true, errors: {} },
});
